import {
  parseStudentBillingAdminBillingUnitDetail,
  parseStudentBillingAdminOverview,
  parseStudentBillingAdminStudentDetail,
  type AdjustStudentBillingAdminUnitAmountRequestDto,
  type AssignStudentBillingAdminPayerRequestDto,
  type CreateOneOffStudentBillingUnitRequest,
  type CreateRegularStudentBillingUnitRequest,
  type CreateStudentBillingAdminPayerRequestDto,
  type LockStudentBillingAdminUnitRequestDto,
  type PutRegularStudentBillingAdminSettingRequestDto,
  type PutStudentBillingCollectionPreferenceRequest,
  type StudentBillingAdminBillingUnitDetailDto,
  type StudentBillingAdminOverviewDto,
  type StudentBillingAdminStudentDetailDto,
  parseStudentBillingPaymentExecutionDto,
  type StudentBillingPaymentExecutionDto
} from "@madamuh/domain";
import {
  apiGet,
  apiPatchWithHeaders,
  apiPostWithHeaders,
  apiPutWithHeaders
} from "./client";

function mutationHeaders(): Record<string, string> {
  return { "Idempotency-Key": crypto.randomUUID() };
}

function root(tenantSlug: string): string {
  return `/api/v1/admin/tenants/${encodeURIComponent(tenantSlug)}/billing`;
}

export async function fetchStudentBillingAdminOverview(tenantSlug: string): Promise<StudentBillingAdminOverviewDto> {
  return parseStudentBillingAdminOverview(await apiGet<unknown>(root(tenantSlug)));
}

export async function fetchStudentBillingAdminStudent(tenantSlug: string, studentId: string): Promise<StudentBillingAdminStudentDetailDto> {
  return parseStudentBillingAdminStudentDetail(await apiGet<unknown>(`${root(tenantSlug)}/students/${encodeURIComponent(studentId)}`));
}

export async function fetchStudentBillingAdminUnit(tenantSlug: string, billingUnitId: string): Promise<StudentBillingAdminBillingUnitDetailDto> {
  return parseStudentBillingAdminBillingUnitDetail(await apiGet<unknown>(`${root(tenantSlug)}/units/${encodeURIComponent(billingUnitId)}`));
}

export async function createStudentBillingAdminPayer(tenantSlug: string, request: CreateStudentBillingAdminPayerRequestDto): Promise<void> {
  await apiPostWithHeaders(`${root(tenantSlug)}/payers`, request, mutationHeaders());
}

export async function assignStudentBillingAdminPayer(
  tenantSlug: string,
  studentId: string,
  request: AssignStudentBillingAdminPayerRequestDto
): Promise<void> {
  await apiPostWithHeaders(`${root(tenantSlug)}/students/${encodeURIComponent(studentId)}/payer-assignments`, request, mutationHeaders());
}

export async function putRegularStudentBillingAdminSetting(
  tenantSlug: string,
  studentId: string,
  request: PutRegularStudentBillingAdminSettingRequestDto
): Promise<void> {
  await apiPutWithHeaders(`${root(tenantSlug)}/students/${encodeURIComponent(studentId)}/regular-setting`, request, mutationHeaders());
}

export async function putStudentBillingAdminCollectionPreference(
  tenantSlug: string,
  studentId: string,
  request: PutStudentBillingCollectionPreferenceRequest
): Promise<void> {
  await apiPutWithHeaders(`${root(tenantSlug)}/students/${encodeURIComponent(studentId)}/collection-preference`, request, mutationHeaders());
}

export async function createRegularStudentBillingAdminUnit(tenantSlug: string, request: CreateRegularStudentBillingUnitRequest): Promise<void> {
  await apiPostWithHeaders(`${root(tenantSlug)}/units/regular`, request, mutationHeaders());
}

export async function createOneOffStudentBillingAdminUnit(tenantSlug: string, request: CreateOneOffStudentBillingUnitRequest): Promise<void> {
  await apiPostWithHeaders(`${root(tenantSlug)}/units/one-off`, request, mutationHeaders());
}

export async function adjustStudentBillingAdminUnit(
  tenantSlug: string,
  billingUnitId: string,
  request: AdjustStudentBillingAdminUnitAmountRequestDto
): Promise<void> {
  await apiPatchWithHeaders(`${root(tenantSlug)}/units/${encodeURIComponent(billingUnitId)}`, request, mutationHeaders());
}

export async function lockStudentBillingAdminUnit(
  tenantSlug: string,
  billingUnitId: string,
  request: LockStudentBillingAdminUnitRequestDto
): Promise<void> {
  await apiPostWithHeaders(`${root(tenantSlug)}/units/${encodeURIComponent(billingUnitId)}/lock`, request, mutationHeaders());
}

export async function fetchStudentBillingAdminPaymentExecution(
  tenantSlug: string,
  billingUnitId: string
): Promise<StudentBillingPaymentExecutionDto | null> {
  const value = await apiGet<unknown>(`${root(tenantSlug)}/units/${encodeURIComponent(billingUnitId)}/payment-executions`);
  return value === null ? null : parseStudentBillingPaymentExecutionDto(value);
}

export async function executeStudentBillingAdminPayment(
  tenantSlug: string,
  billingUnitId: string,
  expectedBillingUnitVersion: number
): Promise<StudentBillingPaymentExecutionDto> {
  return parseStudentBillingPaymentExecutionDto(await apiPostWithHeaders(
    `${root(tenantSlug)}/units/${encodeURIComponent(billingUnitId)}/payment-executions`,
    { expectedBillingUnitVersion },
    mutationHeaders()
  ));
}

export async function retryStudentBillingAdminPayment(
  tenantSlug: string,
  billingUnitId: string,
  executionId: string,
  expectedBillingUnitVersion: number
): Promise<StudentBillingPaymentExecutionDto> {
  return parseStudentBillingPaymentExecutionDto(await apiPostWithHeaders(
    `${root(tenantSlug)}/units/${encodeURIComponent(billingUnitId)}/payment-executions/${encodeURIComponent(executionId)}/retry`,
    { expectedBillingUnitVersion },
    mutationHeaders()
  ));
}
